import { useEffect, useRef, useCallback } from 'react';

/**
 * Scroll-triggered reveal hook using IntersectionObserver.
 * Adds a `data-revealed="true"` attribute when elements enter the viewport.
 *
 * Usage:
 *   const revealRef = useScrollReveal({ threshold: 0.15 });
 *   <div ref={revealRef} className={styles.revealUp}>...</div>
 *
 * For staggered children, set data-stagger-delay on each child or use
 * CSS custom property --stagger-index.
 */
export default function useScrollReveal({ threshold = 0.15, rootMargin = '0px 0px -40px 0px', once = true } = {}) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Respect prefers-reduced-motion
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mq.matches) {
      el.setAttribute('data-revealed', 'true');
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.setAttribute('data-revealed', 'true');
          if (once) observer.unobserve(el);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold, rootMargin, once]);

  return ref;
}

/**
 * Variant: observe multiple children of a container for staggered reveals.
 * Each child gets data-revealed="true" with a stagger delay.
 */
export function useStaggerReveal({ threshold = 0.1, rootMargin = '0px 0px -40px 0px', staggerMs = 100, selector = ':scope > *' } = {}) {
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const children = container.querySelectorAll(selector);

    if (mq.matches) {
      children.forEach((child) => child.setAttribute('data-revealed', 'true'));
      return;
    }

    // Set stagger index CSS variable on each child
    children.forEach((child, i) => {
      child.style.setProperty('--stagger-index', i);
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = parseInt(entry.target.style.getPropertyValue('--stagger-index'), 10) || 0;
            setTimeout(() => {
              entry.target.setAttribute('data-revealed', 'true');
            }, idx * staggerMs);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold, rootMargin }
    );

    children.forEach((child) => observer.observe(child));
    return () => observer.disconnect();
  }, [threshold, rootMargin, staggerMs, selector]);

  return containerRef;
}

/**
 * Hook to track which section is currently in view (for navbar active state).
 * Returns the id of the section currently most visible.
 */
export function useActiveSection(sectionIds, offset = 100) {
  const callbackRef = useRef(null);

  const setCallback = useCallback((cb) => {
    callbackRef.current = cb;
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      let current = sectionIds[0];
      for (const id of sectionIds) {
        const el = document.getElementById(id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= offset) {
            current = id;
          }
        }
      }
      if (callbackRef.current) callbackRef.current(current);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial check
    return () => window.removeEventListener('scroll', handleScroll);
  }, [sectionIds, offset]);

  return setCallback;
}
